import React, { FC, FocusEventHandler } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import calendar from '../../assets/images/icons/calendar.svg';
import { TextField, useTheme } from '@mui/material';
import { useAppTranslation } from '../../i18n';
import { AppEnv } from '../../constants/env';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { deAT, enGB, enUS } from 'date-fns/locale';

const isDev = process.env['REACT_APP_ENV'] === AppEnv.DEV;

const INPUT_FORMAT = 'dd.MM.yyyy';

interface CustomDatePickerProps {
	id: string;
	value: Date | null;
	placeholder?: string;
	disabled?: boolean;
	error?: boolean;
	maxDate?: Date;
	onChange: (value: Date | null) => void;
	onBlur?: FocusEventHandler<HTMLInputElement>;
}

const CustomDatePicker: FC<CustomDatePickerProps> = ({
	id,
	value,
	placeholder,
	onChange,
	onBlur,
	maxDate,
	disabled = false,
	error = false
}) => {
	const theme = useTheme();
	const {
		t,
		i18n: { language }
	} = useAppTranslation();

	const locale = getAdapterLocale(language);
	const toolbarFormat = getToolbarFormat(language);

	const handleChange = (date: Date | null): void => {
		onChange(date);
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
			<DatePicker
				value={value}
				label={placeholder}
				mask="__.__.____"
				inputFormat={INPUT_FORMAT}
				disabled={disabled}
				maxDate={maxDate}
				defaultCalendarMonth={maxDate}
				onChange={handleChange}
				desktopModeMediaQuery={isDev ? 'not (pointer: coarse)' : undefined}
				components={{
					OpenPickerIcon: () => <img src={calendar} className="calendar-open-image" alt="calendar-image" />
				}}
				toolbarFormat={toolbarFormat}
				renderInput={(params) => (
					<TextField
						id={id}
						{...params}
						placeholder={placeholder}
						variant="filled"
						sx={{
							width: '100%',
							mt: 2,
							input: { color: error ? '#ff0000' : 'black' },
							'&:focus-within': {
								border: `1px solid ${theme.palette.primary.main} !important`
							},
							...(error && { border: '1px solid red !important' })
						}}
						inputProps={{
							...params.inputProps,
							placeholder: t('inputs.date-input-format')
						}}
						InputLabelProps={{
							style: {
								color: error ? '#ff0000' : '#707070',
								fontSize: '18px'
							}
						}}
						className={`txt-field-date-picker ${error ? 'Mui-error' : ''}`}
						onBlur={onBlur}
					/>
				)}
			/>
		</LocalizationProvider>
	);
};

export default React.memo(CustomDatePicker);

type AdapterLocale = typeof deAT | typeof enGB | typeof enUS;

const getAdapterLocale = (language: string): AdapterLocale => {
	return language === 'de' ? deAT : enGB;
};

const getToolbarFormat = (language: string) => {
	return language === 'de' ? 'dd, d. MMM' : 'ddd, MMM d';
};
